import { Button, Skeleton, Tabs } from '@mantine/core';
import { IconFileText, IconX } from '@tabler/icons-react';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDay } from '../../format.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { StatusBadge } from '../../StatusBadge.js';
import table from '../materials/Materials.module.css';
import { errorCode } from '../personnel/source.js';
import { GoodsReceiptStatusBadge } from './GoodsReceiptList.js';
import orders from './Orders.module.css';
import { GRN_LINE_TONE, receiptLineBooking } from './orders.js';
import { PasswordDialog } from './PasswordDialog.js';
import classes from './Purchasing.module.css';
import { ReceiptCreated } from './ReceiptCreated.js';
import { formatQuantity, formatStampLocal } from './requisitions.js';
import type { GoodsReceiptSource } from './source.js';
import { LoadError } from './table.js';
import type { GoodsReceiptDetail } from './types.js';

type Tab = 'items' | 'details';

/**
 * Mal kabulü kartı (`satin-alma-akisi.jpeg` "Delivery & GRN"; ADR 0040 #183–#187): GRN numarası,
 * durum ve teslim günü; PO No / Supplier / Delivery Note ve Received By / Received Date kutuları;
 * Items (Ordered / Received / UOM / Status) ve "GRN created". Gemide gerekçeli iptal (stok ters
 * hareketle düzelir); ofiste salt okunur.
 */
export function GoodsReceiptDetailView({
  source,
  id,
  message,
  onBack,
  onOpenOrder,
}: {
  source: GoodsReceiptSource;
  id: string;
  message?: string | null;
  onBack: () => void;
  onOpenOrder?: (purchaseOrderId: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`purchasing.grn.detail.${key}`, options);
  const [detail, setDetail] = useState<GoodsReceiptDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(message ?? null);
  const [cancelling, setCancelling] = useState(false);
  const [tab, setTab] = useState<Tab>('items');

  const load = useCallback(async () => {
    setError(null);
    try {
      setDetail(await source.get(id));
    } catch (failure) {
      setError(errorCode(failure));
    }
  }, [source, id]);
  useEffect(() => {
    void load();
  }, [load]);

  if (error) {
    return (
      <div className={classes.screen}>
        <PageHeader title={k('title')} />
        <LoadError title={k('error')} code={error} onRetry={() => void load()} />
      </div>
    );
  }
  if (!detail) {
    return (
      <div className={classes.screen} aria-busy>
        <span className={classes.visuallyHidden}>{k('loading')}</span>
        <PageHeader title={k('title')} />
        <Skeleton height={190} />
        <Skeleton height={220} />
      </div>
    );
  }

  const editor = source.editor;
  const quantity = (value: number) => formatQuantity(value, locale);
  const issues = detail.lines.some((line) => line.damaged > 0 || line.missing > 0);
  const order = detail.purchaseOrder;

  return (
    <div className={classes.screen}>
      <PageHeader
        title={k('title')}
        description={k('description')}
        crumb={detail.grnNo}
        actions={
          editor && detail.can.cancel ? (
            <div className={classes.headerActions}>
              <Button
                variant="default"
                leftSection={<IconX size={16} />}
                onClick={() => setCancelling(true)}
              >
                {k('cancel')}
              </Button>
            </div>
          ) : undefined
        }
      />

      {notice ? (
        <p className={classes.notice} data-tone="success" role="status">
          {notice}
        </p>
      ) : null}
      {detail.cancelledAt ? (
        <p className={classes.notice} data-tone="danger">
          {k('cancelledNote', { reason: detail.cancelReason ?? '' })}
        </p>
      ) : null}

      <Panel aria-label={k('title')}>
        <div className={orders.head}>
          <div className={orders.headTitle}>
            <h2>{detail.grnNo}</h2>
            <GoodsReceiptStatusBadge status={detail.status} />
          </div>
          <span className={orders.headDate}>{formatDay(detail.receivedDate)}</span>
        </div>

        <div className={orders.stack}>
          <div className={orders.cards} data-grn>
            <Box label={k('poNo')}>
              {onOpenOrder ? (
                <a
                  className={orders.cardLink}
                  href="?"
                  aria-label={k('openPo', { no: order.poNo })}
                  onClick={(event) => {
                    event.preventDefault();
                    onOpenOrder(order.id);
                  }}
                >
                  {order.poNo}
                </a>
              ) : (
                order.poNo
              )}
            </Box>
            <Box label={k('supplier')}>{order.supplierName}</Box>
            <Box label={k('deliveryNote')}>{detail.deliveryNote ?? '—'}</Box>
          </div>
          <div className={orders.cards} data-second>
            <Box label={k('receivedBy')}>{detail.receivedBy.name}</Box>
            <Box label={k('receivedDate')}>{formatDay(detail.receivedDate)}</Box>
          </div>
        </div>

        <Tabs
          value={tab}
          onChange={(value) => setTab((value ?? 'items') as Tab)}
          className={classes.tabs}
          keepMounted={false}
        >
          <Tabs.List aria-label={k('tabsLabel')}>
            <Tabs.Tab value="items">{k('tabs.items')}</Tabs.Tab>
            <Tabs.Tab value="details" leftSection={<IconFileText size={14} aria-hidden />}>
              {k('tabs.details')}
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="items" className={classes.tabBody}>
            <TableBox label={k('tabs.items')}>
              <thead>
                <tr>
                  <th scope="col" className={classes.index}>
                    {k('columns.no')}
                  </th>
                  <th scope="col" className={classes.key}>
                    {k('columns.material')}
                  </th>
                  <th scope="col">{k('columns.ordered')}</th>
                  <th scope="col">{k('columns.received')}</th>
                  {issues ? (
                    <>
                      <th scope="col">{k('columns.damaged')}</th>
                      <th scope="col">{k('columns.missing')}</th>
                    </>
                  ) : null}
                  <th scope="col">{k('columns.uom')}</th>
                  <th scope="col">{k('columns.status')}</th>
                </tr>
              </thead>
              <tbody>
                {detail.lines.map((line) => (
                  <tr key={line.id}>
                    <td className={classes.index}>{line.lineNo}</td>
                    <td className={`${classes.wrap} ${classes.key}`}>{line.name}</td>
                    <td>{quantity(line.ordered)}</td>
                    <td>{quantity(line.accepted)}</td>
                    {issues ? (
                      <>
                        <td>{quantity(line.damaged)}</td>
                        <td>{quantity(line.missing)}</td>
                      </>
                    ) : null}
                    <td>{line.unit}</td>
                    <td>
                      <StatusBadge tone={GRN_LINE_TONE[line.status]}>
                        {t(`purchasing.grn.lineStatuses.${line.status}`)}
                      </StatusBadge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </TableBox>
            <ReceiptCreated
              label={k('created')}
              meta={k('createdBy', {
                name: detail.createdByName ?? detail.receivedBy.name,
                at: formatStampLocal(detail.createdAt),
              })}
            />
          </Tabs.Panel>

          <Tabs.Panel value="details" className={classes.tabBody}>
            <TableBox label={k('tabs.details')}>
              <thead>
                <tr>
                  <th scope="col" className={classes.key}>
                    {k('columns.material')}
                  </th>
                  <th scope="col">{k('columns.condition')}</th>
                  <th scope="col">{k('batch')}</th>
                  <th scope="col">{k('serial')}</th>
                  <th scope="col">{k('expiry')}</th>
                  <th scope="col">{k('remarks')}</th>
                </tr>
              </thead>
              <tbody>
                {detail.lines.map((line) => (
                  <tr key={line.id}>
                    <td className={`${classes.wrap} ${classes.key}`}>
                      {line.name}
                      <span className={classes.muted}>
                        {' · '}
                        {k(receiptLineBooking(line))}
                      </span>
                    </td>
                    <td>{t(`purchasing.grn.conditions.${line.condition}`)}</td>
                    <td>{line.batchNo ?? '—'}</td>
                    <td>{line.serialNo ?? '—'}</td>
                    <td>{line.noExpiry ? k('noExpiry') : formatDay(line.expiryDate)}</td>
                    <td className={classes.wrap}>{line.remarks ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </TableBox>
            <dl className={classes.facts}>
              <div>
                <dt>{k('remarks')}</dt>
                <dd>{detail.remarks ?? '—'}</dd>
              </div>
              <div>
                <dt>{t('purchasing.po.list.columns.vessel')}</dt>
                <dd>{detail.vesselName}</dd>
              </div>
            </dl>
          </Tabs.Panel>
        </Tabs>
      </Panel>

      <div>
        <Button variant="subtle" onClick={onBack}>
          {k('back')}
        </Button>
      </div>

      {editor ? (
        <PasswordDialog
          errorScope="grn"
          opened={cancelling}
          title={k('cancelTitle', { no: detail.grnNo })}
          body={k('cancelBody')}
          reason="required"
          password={false}
          confirmLabel={k('cancel')}
          onClose={() => setCancelling(false)}
          onConfirm={async ({ reason }) => {
            try {
              const next = await editor.cancel(detail.id, {
                rowVersion: detail.rowVersion,
                reason,
              });
              setDetail(next);
              setCancelling(false);
              setNotice(k('notices.cancelled', { no: next.grnNo }));
              return null;
            } catch (failure) {
              return errorCode(failure);
            }
          }}
        />
      ) : null}
    </div>
  );
}

function Box({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className={orders.card}>
      <span className={orders.cardLabel}>{label}</span>
      <span className={orders.cardValue}>{children}</span>
    </div>
  );
}

function TableBox({ label, children }: { label: string; children: ReactNode }) {
  return (
    <section
      className={`${table.tableWrap} ${classes.boxed}`}
      aria-label={label}
      // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
      tabIndex={0}
    >
      <table className={`${table.table} ${classes.table}`}>{children}</table>
    </section>
  );
}
